import React, { useState, useEffect } from "react";
import i18n from "i18next";
import { baseUrl } from "../../helpers/constants";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { ITool } from "../../Models/ITool";
import useWindowSize from "../../helpers/useWindowSize";
import { getPositionsForConfig } from "./GetPositionsForConfig";
import { getGesamtwirkungsgrad } from "./GetGesamtWirkungsgrad";
import { getUebersetzungsverhaeltnis } from "./GetUebersetzungsverhaeltnis";
import { IKonfigurationSt } from "../../Models/IKonfigurationSt";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  Field,
  Dropdown,
  Input,
  Option,
  Button,
  Tooltip,
  Text,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Label,
} from "@fluentui/react-components";
import { ILuebOption } from "../../Models/ILuebOption";
import { AddRegular, DeleteRegular, SaveRegular } from "@fluentui/react-icons";
import LuebPagedDataGrid from "../../Controls/LuebPagedDataGrid";
import ToolSelectionColumns from "./ToolSelectionColumns";

interface IToolSelectionProps {
  configurationParameter: any;
  onSelectionChanged: any;
  preiseSichtbar: boolean;
}

function ToolSelection(props: IToolSelectionProps) {
  const windowSize = useWindowSize();
  const [bauform, setBauform] = useState("all");
  const [hersteller, setHersteller] = useState("all");
  const [energie, setEnergie] = useState("all");

  const [loading, setLoading] = useState<boolean>(false);

  const [showOwnTool, setShowOwnTool] = useState<boolean>(false);
  const [benennungTool, setBenennungTool] = useState<string>("");

  const [benennung, setBenennung] = useState<string>("");

  const [bauformOptions, setBauformOptions] = useState<ILuebOption[]>([]);
  const [herstellerOptions, setHerstellerOptions] = useState<ILuebOption[]>([]);
  const [energieOptions, setEnergieOptions] = useState<ILuebOption[]>([]);

  const [tools, setTools] = useState<ITool[]>([]);
  const [selectedTool, setSelectedTool] = useState<ITool | undefined>(
    undefined,
  );
  const [selectedAdapter, setSelectedAdapter] = useState<any>(undefined);

  const [toolProvision, setToolProvision] = useState(true);

  useEffect(() => {
    getTools();
  }, [benennung, bauform, hersteller, energie, benennungTool]);

  useEffect(() => {
    if (!showOwnTool) {
      getTools();
    }
  }, [showOwnTool]);

  const getDropDownItems = () => {
    const _bauformOptions: ILuebOption[] = [
      { key: "all", text: i18n.t<string>("all") },
    ];
    const _herstellerOptions: ILuebOption[] = [
      { key: "all", text: i18n.t<string>("all") },
    ];
    const _energieOptions: ILuebOption[] = [
      { key: "all", text: i18n.t<string>("all") },
    ];
    tools.forEach((element: ITool) => {
      element.key = element.artikelId;
      const bauformOption: ILuebOption = {
        key: element.bauform,
        text: i18n.t<string>(element.bauform),
      };
      const herstellerOption: ILuebOption = {
        key: element.toolhersteller,
        text: element.toolhersteller,
      };
      const energieOption: ILuebOption = {
        key: element.energie,
        text: i18n.t<string>(element.energie),
      };

      if (!_bauformOptions.some((x) => x.key == bauformOption.key)) {
        _bauformOptions.push(bauformOption);
      }
      if (!_herstellerOptions.some((x) => x.key == herstellerOption.key)) {
        _herstellerOptions.push(herstellerOption);
      }
      if (!_energieOptions.some((x) => x.key == energieOption.key)) {
        _energieOptions.push(energieOption);
      }
    });

    if (bauformOptions.length <= 1) {
      setBauformOptions(_bauformOptions);
    }

    if (herstellerOptions.length <= 1) {
      setHerstellerOptions(_herstellerOptions);
    }

    if (energieOptions.length <= 1) {
      setEnergieOptions(_energieOptions);
    }
  };

  const winkelKopfKab =
    props.configurationParameter?.selectedAngleHead?.selectedAngleHead
      ?.kennzahlAntrieb;
  const baukasten =
    props.configurationParameter?.lsp3Selection?.items?.selectedLsp3Article
      ?.baukasten;

  useEffect(() => {
    getDropDownItems();
  }, [tools]);

  // Wenn Winkelkopf -> über KAN vom Winkelkopf auf KAB vom Tool
  // Wenn kein Winkelkopf alle Adapter suchen und dann entsprechend die Tools filtern

  const getTools = () => {
    setLoading(true);
    // Erstmal Alle Positionen ermitteln:
    const positionen = getPositionsForConfig(props.configurationParameter);

    const configuration: IKonfigurationSt = {
      withProtectiveCoating: false,
      directionOfOutputSocketGear: "unten",
      pruefDrehMoment: 0,
      mountingAngle: 0,
      menge: 1,
      status: 0,
      infoAnVertrieb: "",
      positionen: positionen,
      customToolBenennung: "",
      parameter: [],
    };

    fetchWrapper
      .postWithAuth(
        baseUrl + "/api/configurator/CompleteKonfiguration",
        configuration,
      )
      .then((result: IKonfigurationSt) => {
        // Wirkungsgrad und Übersetzung ermitteln:
        const wirkungsgrad = getGesamtwirkungsgrad(result.positionen);
        const uebersetzung = getUebersetzungsverhaeltnis(result.positionen);
        const antriebsmoment =
          props.configurationParameter.anzugsmoment.nm *
          wirkungsgrad *
          uebersetzung;
        const selectedGearbox = props.configurationParameter.gearbox.key; // z.B.Standard_FL_G
        const luebbWk =
          !props.configurationParameter.selectedAngleHead.withOutAngleHead; // true, false,
        const fremdWk =
          props.configurationParameter.selectedAngleHead.nonLuebAngleHead;

        fetchWrapper
          .getWithAuth(
            `${baseUrl}/api/Configurator/GetTools?antriebsmoment=${antriebsmoment}&selectedGearbox=${selectedGearbox}&luebbWk=${luebbWk}&fremdWk=${fremdWk}&bauform=${bauform}&hersteller=${hersteller}&energie=${energie}&benennung=${
              benennungTool ? benennungTool : benennung
            }`,
          )
          .then((result: ITool[]) => {
            setTools(result);
          })
          .catch((error) => {})
          .finally(() => {
            setLoading(false);
          });
      });

    // Antriebsmoment für Tool = angegebenes Anzugsmoment * Wirkungsgrad * Übersetzung
  };

  const getAdapter = () => {
    // Hier müssen wir unterscheiden ob es ein Tool-FL Adapter ist oder ein Axial Adapter (welcher dem Kunden nicht angezeigt werden darf)
    if (winkelKopfKab) {
      // TODO: Winkelkopf Axial Adapter suchen
    } else if (baukasten) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/Configurator/GetTooLFlAdapter?baukasten=${baukasten}&kan=${selectedTool?.kennzahlAbtrieb}`,
        )
        .then((result: any) => {
          setSelectedAdapter(result);
        })
        .catch((error) => {});
    }
  };

  useEffect(() => {
    getAdapter();
  }, [selectedTool]);

  useEffect(() => {
    props.onSelectionChanged({
      customToolBenennung: benennungTool,
      tool: selectedTool,
      adapter: selectedAdapter,
      toolProvision: toolProvision,
    });
  }, [selectedTool, selectedAdapter]);

  const columns = ToolSelectionColumns(props.preiseSichtbar && !toolProvision);

  return (
    <>
      <Dialog open={showOwnTool}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.t<string>("addOwnTool")}</DialogTitle>
            <DialogContent>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Label>{i18n.t<string>("Benennung")}</Label>
                <Input
                  value={benennungTool}
                  onChange={(ev, data) => {
                    setBenennungTool(data.value);
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowOwnTool(false);
                  setBenennungTool("");
                }}
              >
                {i18n.t<string>("cancel")}
              </Button>
              <Button
                icon={<SaveRegular />}
                appearance="primary"
                onClick={() => {
                  setShowOwnTool(false);
                }}
              >
                {i18n.t<string>("save")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <StackShim horizontal tokens={{ childrenGap: 5 }} wrap={true}>
        <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
          <Field label={i18n.t<string>("Benennung")}>
            <Input
              value={benennung}
              onChange={(ev, data) => {
                setBenennung(data.value ? data.value : "");
              }}
            />
          </Field>
        </StackItemShim>
        <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
          <Field label={i18n.t<string>("bauform")}>
            <Dropdown
              value={
                bauformOptions.find((x) => x.key == bauform)?.text
                  ? bauformOptions.find((x) => x.key == bauform)?.text
                  : bauform
              }
              selectedOptions={[bauform]}
              defaultSelectedOptions={[bauform]}
              onOptionSelect={(ev, data) => {
                setBauform(data.optionValue ? data.optionValue : "all");
              }}
            >
              {bauformOptions.map((item) => {
                if (typeof item.key == "string") {
                  return <Option value={item.key}>{item.text}</Option>;
                }
              })}
            </Dropdown>
          </Field>
        </StackItemShim>
        <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
          <Field label={i18n.t<string>("hersteller")}>
            <Dropdown
              value={
                herstellerOptions.find((x) => x.key == hersteller)?.text
                  ? herstellerOptions.find((x) => x.key == hersteller)?.text
                  : hersteller
              }
              selectedOptions={[hersteller]}
              defaultSelectedOptions={[hersteller]}
              onOptionSelect={(ev, data) => {
                setHersteller(data.optionValue ? data.optionValue : "all");
              }}
            >
              {herstellerOptions.map((item) => {
                if (typeof item.key == "string") {
                  return <Option value={item.key}>{item.text}</Option>;
                }
              })}
            </Dropdown>
          </Field>
        </StackItemShim>
        <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
          <Field label={i18n.t<string>("energie")}>
            <Dropdown
              value={
                energieOptions.find((x) => x.key == energie)?.text
                  ? energieOptions.find((x) => x.key == energie)?.text
                  : energie
              }
              selectedOptions={[energie]}
              defaultSelectedOptions={[energie]}
              onOptionSelect={(ev, data) => {
                setEnergie(data.optionValue ? data.optionValue : "all");
              }}
            >
              {energieOptions.map((item) => {
                if (typeof item.key == "string") {
                  return <Option value={item.key}>{item.text}</Option>;
                }
              })}
            </Dropdown>
          </Field>
        </StackItemShim>
      </StackShim>
      <Field
        style={{
          display: "flex",
          justifyContent: "flex-start",
          marginTop: "5px",
        }}
      >
        {(benennungTool == "" || showOwnTool) && (
          <Button
            appearance="primary"
            icon={<AddRegular />}
            onClick={() => {
              setShowOwnTool(true);
            }}
          >
            {i18n.t<string>("addOwnTool")}
          </Button>
        )}

        {!showOwnTool && benennungTool && (
          <div style={{ marginLeft: "10px", marginTop: "10px" }}>
            {i18n.t<string>("ownTool")}
            {":"} {benennungTool}
          </div>
        )}

        {benennungTool && !showOwnTool && (
          <Tooltip
            content={i18n.t<string>("deleteOwnTool")}
            relationship={"label"}
          >
            <Text>
              <Button
                appearance="transparent"
                icon={<DeleteRegular />}
                onClick={() => {
                  setBenennungTool("");
                }}
              />
            </Text>
          </Tooltip>
        )}
        <Field style={{ marginLeft: "5px" }}>
          <Checkbox
            checked={toolProvision}
            label={i18n.t<string>("toolProvision")}
            onChange={(ev, data) => {
              if (data.checked != "mixed") {
                setToolProvision(data.checked);
              }
            }}
          />
        </Field>
      </Field>
      <div style={{ marginBottom: "10px" }}>
        <LuebPagedDataGrid
          onPageChanged={() => {
            setSelectedTool(undefined);
          }}
          selectionMode="single"
          selectedItems={
            selectedTool ? new Set([tools.indexOf(selectedTool)]) : new Set([])
          }
          onSelectionChange={(ev, data) => {
            const selItem = data.selectedItems;
            tools.forEach((obj, index) => {
              if (selItem.has(index)) {
                setSelectedTool(obj);
              }
            });
          }}
          columns={columns.columns}
          items={tools}
          resizableColumns={true}
          columnSizingOptions={{
            artikelId: { defaultWidth: 85, minWidth: 70 },
            benennungM1: { defaultWidth: 130, minWidth: 130 },
            herstellerId: { defaultWidth: 130, minWidth: 130 },
            checkedId: { defaultWidth: 50, minWidth: 50 },
            gewichtId: { defaultWidth: 65, minWidth: 65 },
            laengeId: { defaultWidth: 50, minWidth: 50 },
            preisId: { defaultWidth: 50, minWidth: 50 },
            maxDrehmomentId: { defaultWidth: 50, minWidth: 50 },
            minDrehmomentId: { defaultWidth: 50, minWidth: 50 },
          }}
        />
      </div>

      {props.preiseSichtbar && !toolProvision && (
        <Text style={{ marginTop: 20 }}>
          <Text style={{ color: "#ff6600", fontWeight: 700 }}>*</Text>
          {i18n.t<string>("priceNote")}
        </Text>
      )}
    </>
  );
}

export default ToolSelection;
